import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";

import { css } from "@emotion/react";
import React, { useCallback } from "react";
import cn from "classnames";
import { CSS_COLORS } from "../../../../../styles/styles-css-variables";
import { CSS_MQ } from "../../../../../styles/breakpoints";

export const ProgressBar: ReactTFC<{
  scrollProgress: number;
  imagesCount: number;
  visiblePerPage: number | null;
  onSeekRequested: (progress: { seek: number; pageIdx: number }) => void;
  placeholderForNonFilledState?: boolean;
}> = props => {
  function getPagesData() {
    if (typeof props.visiblePerPage !== "number") {
      return null;
    }

    return {
      pagesCount:
        props.visiblePerPage >= 1 ? Math.ceil(props.imagesCount / Math.floor(props.visiblePerPage)) : props.imagesCount,
      visiblePerPage: Math.max(1, props.visiblePerPage),
    };
  }

  const data = getPagesData();

  return (
    <React.Fragment>
      {data !== null && data.pagesCount > 1 ? (
        <ProgressBarWithDots
          pagesCount={data.pagesCount}
          visiblePerPage={data.visiblePerPage}
          scrollProgress={props.scrollProgress}
          onSeekRequested={props.onSeekRequested}
          elementsCount={props.imagesCount}
        />
      ) : props.placeholderForNonFilledState ? (
        <span
          css={css`
            display: block;
            height: 16px;
          `}
        />
      ) : null}
    </React.Fragment>
  );
};

const ProgressBarWithDots: ReactTFC<{
  elementsCount: number;
  pagesCount: number;
  visiblePerPage: number;
  scrollProgress: number;
  onSeekRequested: (progress: { seek: number; pageIdx: number }) => void;
}> = props => {
  function getPagesLayout() {
    return new Array(props.pagesCount)
      .fill(undefined)
      .map((_, idx) => idx)
      .map(idx => {
        const isLast = props.pagesCount - 1 === idx;

        let start: number;
        let end: number;
        if (!isLast) {
          start = Math.floor(props.visiblePerPage) * idx;
          end = start + props.visiblePerPage;
        } else {
          end = props.elementsCount;
          start = end - props.visiblePerPage;
        }

        return {
          start,
          end,
        };
      });
  }

  const pagesLayout = getPagesLayout();

  const firstPage = pagesLayout[0]!;
  const lastPage = pagesLayout[pagesLayout.length - 1]!;

  const currentPage = {
    start: firstPage.start + (lastPage.start - firstPage.start) * props.scrollProgress,
    end: firstPage.end + (lastPage.end - firstPage.end) * props.scrollProgress,
  };

  const currentPageIdx = pagesLayout.findIndex(l => l.start >= currentPage.start);

  const scrollingLength = lastPage.start - firstPage.start;

  return (
    <div
      css={css`
        ${CSS_MQ.atMostSm} {
          display: none;
        }

        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-content: center;
      `}
    >
      {pagesLayout.map((layout, idx) => {
        return (
          <button
            key={idx}
            className={cn({ current: idx === currentPageIdx })}
            onClick={() => {
              const seek = layout.start / scrollingLength;

              props.onSeekRequested({ seek, pageIdx: idx });
            }}
            css={css`
              display: block;
              flex-shrink: 0;

              width: 32px;
              height: 32px;
              padding: 10px;
            `}
          >
            <span
              css={css`
                border-radius: 100%;
                display: block;

                width: 100%;
                height: 100%;

                background-color: ${CSS_COLORS.Grey};
                .current & {
                  background-color: ${CSS_COLORS.Green};
                }
              `}
            />
          </button>
        );
      })}
    </div>
  );
};
